/* ==========================================================================
   fiwr-sj.js — Sound Journeys behaviour.

   Loaded ONLY on templates/page.sound-journeys.json (conditional in
   layout/theme.liquid). No dependencies, no CDN, no framework.

   Contents:
     1. Hash router (hub / reset / relax / rejuvenate)
     2. Filter pills + search, composed, client-side
     3. Intention icons -> activate pill + scroll to list
     4. Preview modal (focus trap, Esc, focus return)

   NOTE ON FIRST PAINT: the initial view is resolved by a small synchronous
   bootstrap at the top of sections/fiwr-sj-hub.liquid, which sets a class on
   <html> before any view markup is parsed. That is what stops the hub
   flashing on a cold load of #reset. This file re-applies the same logic
   defensively and then owns every subsequent change.
   ========================================================================== */

(function () {
  'use strict';

  var VIEWS = ['hub', 'reset', 'relax', 'rejuvenate', 'specialized'];

  /* Hashes that name a view we have not built yet. Treated as "stay put"
     rather than "unknown -> hub". Empty since Specialized Wellness shipped. */
  var DEFERRED = [];

  var VIEW_CLASSES = VIEWS.map(function (v) { return 'sj-view-' + v; });

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function hashKey() {
    return (window.location.hash || '').replace(/^#/, '').toLowerCase();
  }

  /* Returns a view name, or null when the hash should not change the view. */
  function resolveView() {
    var key = hashKey();
    if (VIEWS.indexOf(key) !== -1) return key;
    if (DEFERRED.indexOf(key) !== -1) return null;
    return 'hub';
  }

  function currentView() {
    var cls = document.documentElement.classList;
    for (var i = 0; i < VIEWS.length; i++) {
      if (cls.contains('sj-view-' + VIEWS[i])) return VIEWS[i];
    }
    return null;
  }

  function applyView(view) {
    if (!view) return;
    var root = document.documentElement;
    root.classList.remove.apply(root.classList, VIEW_CLASSES);
    root.classList.add('sj-view-' + view);

    var el = document.querySelector('[data-sj-view="' + view + '"]');
    var docTitle = el && el.getAttribute('data-sj-doc-title');
    if (docTitle) document.title = docTitle;
  }

  /* ---------------------------------------------------------------------- 
     1. Router
     ---------------------------------------------------------------------- */
  function initRouter() {
    // Defensive: bootstrap normally did this already.
    if (!currentView()) applyView(resolveView() || 'hub');

    window.addEventListener('hashchange', function () {
      var next = resolveView();
      if (!next || next === currentView()) return;

      applyView(next);

      var wrap = document.querySelector('.fiwr-sj');
      if (wrap) {
        var top = wrap.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: top, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
    });
  }

  /* ----------------------------------------------------------------------
     2 + 3. Per-pathway filtering, search, intention icons
     ---------------------------------------------------------------------- */
  function initPathway(root) {
    var pills = Array.prototype.slice.call(root.querySelectorAll('[data-sj-filter]'));
    var search = root.querySelector('[data-sj-search]');
    var list = root.querySelector('[data-sj-list]');
    if (!list) return;

    var rows = Array.prototype.slice.call(list.querySelectorAll('[data-sj-journey]'));
    var empty = root.querySelector('[data-sj-empty]');
    var state = { filter: 'all', query: '' };

    function render() {
      var shown = 0;

      rows.forEach(function (row) {
        var intention = row.getAttribute('data-sj-intention') || '';
        var title = row.getAttribute('data-sj-title') || '';
        // Token match: dataset rows carry one key, product rows carry all
        // tags space-separated. Padding avoids substring hits (rest/restore).
        var okFilter = state.filter === 'all' ||
          (' ' + intention + ' ').indexOf(' ' + state.filter + ' ') !== -1;
        var okQuery = !state.query || title.indexOf(state.query) !== -1;
        var visible = okFilter && okQuery;

        row.hidden = !visible;
        if (visible) shown++;
      });

      if (empty) empty.hidden = shown !== 0;
    }

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        setFilter(pill.getAttribute('data-sj-filter'));
      });
    });

    function setFilter(value) {
      state.filter = value || 'all';
      pills.forEach(function (p) {
        p.setAttribute('aria-pressed', p.getAttribute('data-sj-filter') === state.filter ? 'true' : 'false');
      });
      render();
    }

    if (search) {
      search.addEventListener('input', function () {
        state.query = search.value.trim().toLowerCase();
        render();
      });
    }

    // Intention icon -> activate the matching pill, then scroll to the list.
    root.querySelectorAll('[data-sj-intent]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setFilter(btn.getAttribute('data-sj-intent'));
        var anchor = root.querySelector('[data-sj-list-anchor]') || list;
        anchor.scrollIntoView({
          behavior: reduceMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });

    setFilter('all');
  }

  /* ----------------------------------------------------------------------
     3b. Specialized Wellness: group icons + needs search over the A–Z grid.
     Same show/hide pattern as the pathway lists. Group buttons and the
     search compose; both act on [data-sj-need] <details> elements.
     ---------------------------------------------------------------------- */
  function initSpecialized(root) {
    var list = root.querySelector('[data-sj-needs-list]');
    if (!list) return;

    var items = Array.prototype.slice.call(list.querySelectorAll('[data-sj-need]'));
    var groups = Array.prototype.slice.call(root.querySelectorAll('[data-sj-group]'));
    var search = root.querySelector('[data-sj-needs-search]');
    var empty = root.querySelector('[data-sj-needs-empty]');
    var state = { group: 'all', query: '' };

    function render() {
      var shown = 0;

      items.forEach(function (item) {
        var group = item.getAttribute('data-sj-group-key') || '';
        var key = item.getAttribute('data-sj-need-key') || '';
        // Token match, same as the pathway lists: collection mode gives a
        // product every group whose collection contains it, space-separated.
        var okGroup = state.group === 'all' ||
          (' ' + group + ' ').indexOf(' ' + state.group + ' ') !== -1;
        var okQuery = !state.query || key.indexOf(state.query) !== -1;
        var visible = okGroup && okQuery;

        item.hidden = !visible;
        if (!visible) item.open = false;
        if (visible) shown++;
      });

      if (empty) empty.hidden = shown !== 0;
    }

    groups.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.group = btn.getAttribute('data-sj-group') || 'all';
        groups.forEach(function (g) {
          var active = g === btn;
          g.classList.toggle('is-active', active);
          g.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        render();

        var anchor = root.querySelector('[data-sj-needs-anchor]') || list;
        anchor.scrollIntoView({
          behavior: reduceMotion() ? 'auto' : 'smooth',
          block: 'start'
        });
      });
    });

    if (search) {
      search.addEventListener('input', function () {
        state.query = search.value.trim().toLowerCase();
        render();
      });
    }

    render();
  }

  /* ----------------------------------------------------------------------
     4. Preview modal
     ---------------------------------------------------------------------- */
  var FOCUSABLE = [
    'a[href]', 'button:not([disabled])', 'input:not([disabled])',
    'audio[controls]', 'textarea:not([disabled])', 'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  function initModal() {
    var modal = document.querySelector('[data-sj-modal]');
    if (!modal) return;

    var panel = modal.querySelector('[data-sj-modal-panel]');
    var titleEl = modal.querySelector('[data-sj-modal-title]');
    var audio = modal.querySelector('[data-sj-modal-audio]');
    var lastTrigger = null;

    function focusables() {
      return Array.prototype.slice.call(panel.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null || el === panel; });
    }

    function open(trigger) {
      var clip = trigger.getAttribute('data-sj-clip');
      if (!clip) return;

      lastTrigger = trigger;
      if (titleEl) titleEl.textContent = trigger.getAttribute('data-sj-clip-title') || '';

      // Optional context lines — populated from the trigger, hidden if empty.
      ['meta', 'duration', 'statement'].forEach(function (name) {
        var el = modal.querySelector('[data-sj-modal-' + name + ']');
        if (!el) return;
        var v = trigger.getAttribute('data-sj-' + name) || '';
        el.textContent = v;
        el.hidden = !v;
      });

      // Premium download CTA — only when the journey has a product URL.
      var productUrl = trigger.getAttribute('data-sj-product') || '';
      var dl = modal.querySelector('[data-sj-modal-download]');
      var fine = modal.querySelector('[data-sj-modal-fine]');
      if (dl) {
        if (productUrl) dl.setAttribute('href', productUrl);
        dl.hidden = !productUrl;
      }
      if (fine) fine.hidden = !productUrl;

      if (audio) {
        audio.src = clip;
        audio.currentTime = 0;
      }

      modal.hidden = false;
      document.addEventListener('keydown', onKeydown, true);
      panel.focus();

      if (audio) {
        var p = audio.play();
        // Autoplay can be refused; the visible controls remain usable.
        if (p && typeof p.catch === 'function') p.catch(function () {});
      }
    }

    function close() {
      if (modal.hidden) return;

      modal.hidden = true;
      document.removeEventListener('keydown', onKeydown, true);

      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
        audio.load();
      }

      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    }

    function onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }

      if (e.key !== 'Tab') return;

      var items = focusables();
      if (!items.length) {
        e.preventDefault();
        panel.focus();
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-sj-preview]');
      if (trigger) {
        e.preventDefault();
        open(trigger);
        return;
      }
      if (e.target.closest('[data-sj-modal-close]')) {
        e.preventDefault();
        close();
      }
    });
  }

  /* ---------------------------------------------------------------------- */
  function init() {
    initRouter();
    document.querySelectorAll('[data-sj-pathway]').forEach(initPathway);
    document.querySelectorAll('[data-sj-specialized]').forEach(initSpecialized);
    initModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
